import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DataTable, PageLayout } from "../../../components/modern";
import type { Column } from "../../../components/modern";
import { adminApi } from "../../../utils/api";
import { showErrorToast } from "../../../utils/toastNotifications";
import {
  ORDER_STATUS_VALUES,
  money,
  statusBadgeClass,
  statusLabel
} from "../orderGroupShared";
import type { OrderGroupStatusCount } from "../orderGroupShared";
import type { OrderGroupKind } from "../orderGroupKinds";
import "./OrderGroupIndex.scss";

interface OrderGroupSummary {
  id: number;
  legacyOrderGroupNumber?: number | null;
  orderType: number;
  originCountryName?: string | null;
  destinationCountryName?: string | null;
  userName?: string | null;
  itemCount: number;
  activeItemCount: number;
  isDangerous: boolean;
  invoiceTotal: number;
  paidInvoiceTotal: number;
  manualRefundTotal: number;
  pendingRefundTotal: number;
  groupTotal: number;
  remainingAmount: number;
  invoiceCount: number;
  statusCounts?: OrderGroupStatusCount[];
  createdAt: string;
}

interface OrderGroupItem {
  id: number;
  orderType: number;
  number?: string | null;
  merchantName?: string | null;
  quantity: number;
  originCountryName?: string | null;
  orderStatus: number;
}

interface OrderGroupIndexProps {
  /** The kind of group this list shows. */
  kind: OrderGroupKind;
}

export const OrderGroupIndex: React.FC<OrderGroupIndexProps> = ({ kind }) => {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const [groups, setGroups] = useState<OrderGroupSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState("");
  const [pendingRefundOnly, setPendingRefundOnly] = useState(false);
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [expandedGroupId, setExpandedGroupId] = useState<number | null>(null);
  const [expandedItems, setExpandedItems] = useState<Record<number, OrderGroupItem[]>>({});
  const [expandedLoading, setExpandedLoading] = useState<number | null>(null);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage.toString(), pageSize: pageSize.toString() });
      if (searchTerm.trim()) params.set("search", searchTerm.trim());
      if (status !== "") params.set("status", status);
      if (pendingRefundOnly) params.set("hasPendingRefund", "true");
      if (createdFrom) params.set("createdFrom", createdFrom);
      if (createdTo) params.set("createdTo", createdTo);
      const response = await adminApi.call(`${kind.api}?${params.toString()}`, "GET");
      if (!response.success) {
        showErrorToast(response.error || t("orderGroups.messages.loadError"));
        return;
      }

      setGroups(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error) {
      console.error("Failed to load order groups", error);
      showErrorToast(t("orderGroups.messages.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [kind, currentPage, pageSize, searchTerm, status, pendingRefundOnly, createdFrom, createdTo]);

  const changeFilter = (apply: () => void) => {
    apply();
    setCurrentPage(1);
    setExpandedGroupId(null);
  };

  const toggleGroup = async (group: OrderGroupSummary) => {
    if (expandedGroupId === group.id) {
      setExpandedGroupId(null);
      return;
    }

    setExpandedGroupId(group.id);
    if (expandedItems[group.id]) return;

    setExpandedLoading(group.id);
    try {
      const response = await adminApi.call(`${kind.api}/${group.id}`, "GET");
      if (!response.success) {
        showErrorToast(response.error || t("orderGroups.messages.loadError"));
        setExpandedGroupId(null);
        return;
      }
      setExpandedItems((current) => ({
        ...current,
        [group.id]: response.data?.items || []
      }));
    } catch (error) {
      console.error("Failed to load order group items", error);
      showErrorToast(t("orderGroups.messages.loadError"));
      setExpandedGroupId(null);
    } finally {
      setExpandedLoading(null);
    }
  };

  const paymentStateLabel = (group: OrderGroupSummary) => {
    if (group.invoiceCount === 0) return t("orderGroups.payment.noInvoice");
    if (group.remainingAmount <= 0) return t("orderGroups.payment.paid");
    return group.paidInvoiceTotal > 0 ? t("orderGroups.payment.partial") : t("orderGroups.payment.pending");
  };

  const renderExpandedRow = (group: OrderGroupSummary) => {
    const items = expandedItems[group.id] || [];

    if (expandedLoading === group.id) {
      return <div className="order-group-expanded-content text-muted">{t("orderGroups.loadingItems")}</div>;
    }

    return (
      <div className="order-group-expanded-content">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>{t("orderGroups.items")}</strong>
          <span className="badge bg-primary">{items.length}</span>
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>{t("orderGroups.columns.number")}</th>
                <th>{t("orderGroups.columns.merchant")}</th>
                <th>{t("orderGroups.columns.origin")}</th>
                <th>{t("orderGroups.columns.quantity")}</th>
                <th>{t("orderGroups.columns.status")}</th>
                <th className="text-end">{t("orderGroups.columns.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={`${item.orderType}-${item.id}`}>
                  <td>{item.number || `#${item.id}`}</td>
                  <td>{item.merchantName || "—"}</td>
                  <td>{item.originCountryName || "—"}</td>
                  <td>{item.quantity}</td>
                  <td><span className={`badge ${statusBadgeClass(item.orderStatus)}`}>{statusLabel(t, item.orderStatus)}</span></td>
                  <td className="text-end">
                    <Link
                      className="btn btn-sm btn-outline-primary"
                      to={`${kind.path}/${group.id}?item=${item.orderType}-${item.id}`}
                    >
                      {t("orderGroups.actions.viewItem")}
                    </Link>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="text-center py-3">{t("orderGroups.emptyItems")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const columns: Column[] = [
    {
      key: "id",
      title: t("orderGroups.columns.group"),
      sortable: true,
      render: (value) => <span className="order-group-link">#{value}</span>
    },
    {
      key: "originCountryName",
      title: t("orderGroups.columns.origin"),
      sortable: true,
      render: (value, row) => `${value || "—"} → ${row.destinationCountryName || "—"}`
    },
    {
      key: "userName",
      title: t("orderGroups.columns.customer"),
      sortable: true,
      render: (value) => value || "—"
    },
    {
      key: "itemCount",
      title: t("orderGroups.columns.items"),
      sortable: true,
      align: "center",
      render: (_, row) => (
        <span title={t("orderGroups.activeOfTotal")}>
          {row.activeItemCount ?? 0} / {row.itemCount ?? 0}
          {row.isDangerous && <span className="badge bg-danger ms-1">DG</span>}
        </span>
      )
    },
    {
      key: "statusCounts",
      title: t("orderGroups.columns.statuses"),
      sortable: false,
      render: (_, row) => (
        <div className="d-flex flex-wrap gap-1">
          {(row.statusCounts || []).map((entry: OrderGroupStatusCount) => (
            <span key={entry.status} className={`badge ${statusBadgeClass(entry.status)}`}>
              {entry.count} · {statusLabel(t, entry.status)}
            </span>
          ))}
        </div>
      )
    },
    {
      key: "groupTotal",
      title: t("orderGroups.columns.total"),
      sortable: true,
      align: "right",
      render: (value) => money(value)
    },
    {
      key: "paidInvoiceTotal",
      title: t("orderGroups.columns.paid"),
      sortable: true,
      align: "right",
      render: (value) => money(value)
    },
    {
      key: "remainingAmount",
      title: t("orderGroups.columns.remaining"),
      sortable: true,
      align: "right",
      render: (value, row) => (
        <span>
          {money(value)}
          {row.pendingRefundTotal > 0 && (
            <small className="d-block text-danger">
              {t("orderGroups.refund.pendingShort")} {money(row.pendingRefundTotal)}
            </small>
          )}
        </span>
      )
    },
    {
      key: "paymentState",
      title: t("orderGroups.columns.payment"),
      sortable: false,
      align: "center",
      render: (_, row) => paymentStateLabel(row)
    },
    {
      key: "createdAt",
      title: t("orderGroups.columns.created"),
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleString() : "—"
    },
    {
      key: "actions",
      title: t("orderGroups.columns.actions"),
      sortable: false,
      align: "center",
      render: (_, row) => (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={(event) => {
            event.stopPropagation();
            navigate(`${kind.path}/${row.id}`);
          }}
        >
          {t("orderGroups.actions.open")}
        </button>
      )
    }
  ];

  return (
    <PageLayout
      title={t(`orderGroups.kinds.${kind.labels}.title`)}
      subtitle={t(`orderGroups.kinds.${kind.labels}.subtitle`)}
      breadcrumbs={[{ label: t(`orderGroups.kinds.${kind.labels}.title`) }]}
      className={i18n.language === "ar" ? "rtl" : "ltr"}
      isIndexPage
    >
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body row g-3 align-items-end">
          <div className="col-md-4">
            <label className="form-label mb-1" htmlFor="order-group-search">{t("orderGroups.filters.search")}</label>
            <input
              id="order-group-search"
              type="search"
              className="form-control"
              value={searchTerm}
              onChange={(event) => changeFilter(() => setSearchTerm(event.target.value))}
              placeholder={t("orderGroups.filters.searchPlaceholder")}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label mb-1" htmlFor="order-group-status">{t("orderGroups.filters.status")}</label>
            <select
              id="order-group-status"
              className="form-select"
              value={status}
              onChange={(event) => changeFilter(() => setStatus(event.target.value))}
            >
              <option value="">{t("orderGroups.filters.allStatuses")}</option>
              {ORDER_STATUS_VALUES.map((value) => (
                <option key={value} value={value}>{statusLabel(t, value)}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label mb-1" htmlFor="order-group-from">{t("orderGroups.filters.from")}</label>
            <input
              id="order-group-from"
              type="date"
              className="form-control"
              value={createdFrom}
              onChange={(event) => changeFilter(() => setCreatedFrom(event.target.value))}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label mb-1" htmlFor="order-group-to">{t("orderGroups.filters.to")}</label>
            <input
              id="order-group-to"
              type="date"
              className="form-control"
              value={createdTo}
              onChange={(event) => changeFilter(() => setCreatedTo(event.target.value))}
            />
          </div>
          <div className="col-12">
            <div className="form-check">
              <input
                id="order-group-pending-refund"
                type="checkbox"
                className="form-check-input"
                checked={pendingRefundOnly}
                onChange={(event) => changeFilter(() => setPendingRefundOnly(event.target.checked))}
              />
              <label className="form-check-label" htmlFor="order-group-pending-refund">
                {t("orderGroups.filters.pendingRefund")}
              </label>
            </div>
            <small className="text-muted">{t("orderGroups.filters.statusHint")}</small>
          </div>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={groups}
        loading={loading}
        searchable={false}
        filterable={false}
        exportable={false}
        refreshable
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onRefresh={fetchGroups}
        rowKey={(row) => row.id}
        onRowClick={toggleGroup}
        expandedRowKey={expandedGroupId}
        renderExpandedRow={renderExpandedRow}
        emptyMessage={t("orderGroups.empty")}
      />
    </PageLayout>
  );
};
